import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Rocket, TrendingUp, Lightbulb, ChevronRight } from 'lucide-react';
export function OnboardingSlide() {
  const [activePhase, setActivePhase] = useState<number | null>(null);
  const phases = [{
    id: 1,
    title: 'Foundations Call',
    time: 'Week 1',
    icon: Target,
    desc: 'Access to LetLois and teach you the basics to get you immediate wins',
    outcomes: ['Custom roadmap built', 'Quick wins identified', 'Timeline established'],
    duration: '60 minutes'
  }, {
    id: 2,
    title: 'Website Launch',
    time: 'Weeks 2-4',
    icon: Rocket,
    desc: 'Your build will be complete; you can then review and adjust',
    outcomes: ['3-layer optimized website live', 'Conversion tracking installed', 'First leads coming in'],
    duration: '45 minutes'
  }, {
    id: 3,
    title: 'Next Steps Coaching',
    time: 'Week 4',
    icon: TrendingUp,
    desc: 'The team makes any last-minute changes to the process',
    outcomes: ['Performance review', 'Strategy refinement', 'Decision point (Grow/Boost/Delegate)'],
    duration: '60 minutes'
  }, {
    id: 4,
    title: 'Support & Coaching',
    time: 'Ongoing',
    icon: Lightbulb,
    desc: "We'll help you stay focused and your tech to just work",
    outcomes: ['Continuous optimization', 'Team alignment', 'Scaling strategy'],
    duration: '30 minutes / once a month'
  }];
  return <section id="process" className="min-h-screen bg-dark-section py-24 px-4 flex flex-col justify-center" data-id="element-316" data-mp="bocm35rmv3un">
      <div className="max-w-7xl mx-auto w-full" data-id="element-317" data-mp="3ttpkoo1us55i">
        <div className="text-center mb-16" data-id="element-318" data-mp="3tw2bzrti3rv8">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-319" data-mp="2o7rxxbga1yf">
            Your Journey with LetLois
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-320" data-mp="1qlz1084oamne">
            From signup to results in weeks, not months
          </motion.p>
        </div>

        {/* Timeline Container */}
        <div className="relative py-12 mb-24" data-id="element-321" data-mp="29eb0mn3xz9tu">
          {/* Connecting Line (Desktop) */}
          <div className="hidden md:block absolute top-[32px] left-0 w-full h-1 bg-white/10 rounded-full overflow-hidden z-0" data-id="element-322" data-mp="12trz6tg6hngl">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '100%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeInOut'
          }} className="h-full bg-primary" data-id="element-323" data-mp="nt382xrmgxaw" />
          </div>

          {/* Connecting Line (Mobile) */}
          <div className="md:hidden absolute top-0 left-8 w-1 h-full bg-white/10 -translate-x-1/2 rounded-full overflow-hidden z-0" data-id="element-324" data-mp="16lfa1jed5wki">
            <motion.div initial={{
            height: 0
          }} whileInView={{
            height: '100%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeInOut'
          }} className="w-full bg-primary" data-id="element-325" data-mp="1ghs3cg5zy8eq" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-4 relative z-10" data-id="element-326" data-mp="1himt1jkv4ces">
            {phases.map((phase, index) => <motion.div key={phase.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className="relative pl-20 md:pl-0 group" onMouseEnter={() => setActivePhase(phase.id)} onMouseLeave={() => setActivePhase(null)} data-id="element-327" data-mp="1cmpbxb1rz20y" data-mpi={index}>
                {/* Node */}
                <div className={`absolute left-0 md:left-1/2 top-0 md:top-0 md:-translate-x-1/2 w-16 h-16 rounded-full border-4 border-dark bg-dark-card flex items-center justify-center transition-all duration-300 z-20 ${activePhase === phase.id ? 'scale-110 border-primary shadow-[0_0_20px_rgba(26,139,246,0.5)]' : 'border-white/10'}`} data-id="element-328" data-mp="f4fi9zhwauxe">
                  <phase.icon size={24} className={`transition-colors duration-300 ${activePhase === phase.id ? 'text-primary' : 'text-text-muted'}`} data-id="element-329" data-mp="2csrrw3acqu7x" />
                </div>

                {/* Time label below node on desktop */}
                <div className="hidden md:block text-center mt-20 mb-3" data-id="element-330" data-mp="35fm98rbvhdh6">
                  <span className="text-xs font-bold text-text-dim uppercase tracking-wider" data-id="element-331" data-mp="39i930xs3t8h0">
                    {phase.time}
                  </span>
                </div>

                {/* Time label above card on mobile */}
                <div className="md:hidden absolute -top-6 left-20 text-xs font-bold text-text-dim uppercase tracking-wider" data-id="element-332" data-mp="1k43y1jbjewir">
                  {phase.time}
                </div>

                {/* Content Card */}
                <div className={`md:mt-2 bg-dark-card border ${activePhase === phase.id ? 'border-primary/50' : 'border-white/10'} p-6 rounded-xl transition-all duration-300 hover:bg-white/5 relative`} data-id="element-333" data-mp="151m1gbo3ts6n">
                  <span className="text-4xl font-bold text-white/5 absolute right-4 top-4 select-none" data-id="element-334" data-mp="w77oifr1qtqp">
                    0{phase.id}
                  </span>
                  <h3 className={`text-lg font-bold transition-colors mb-2 ${activePhase === phase.id ? 'text-primary' : 'text-white'}`} data-id="element-335" data-mp="mb4c8h2hdeq1">
                    {phase.title}
                  </h3>

                  <p className="text-sm text-text-muted mb-4 leading-relaxed" data-id="element-336" data-mp="daprbitfi1n2">
                    {phase.desc}
                  </p>

                  <AnimatePresence data-id="element-337" data-mp="8d6cohstjmul">
                    {activePhase === phase.id && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-338" data-mp="27luy2vztau6m">
                        <ul className="space-y-2 mb-4" data-id="element-339" data-mp="1r9rgjvb72rt5">
                          {phase.outcomes.map((outcome, i) => <li key={i} className="flex items-start text-xs text-text-dim" data-id="element-340" data-mp="1w7t0eigg2v5o" data-mpi={i}>
                              <ChevronRight size={12} className="mr-1 mt-0.5 text-primary shrink-0" data-id="element-341" data-mp="2590zh2qsc00t" />
                              {outcome}
                            </li>)}
                        </ul>
                        <div className="text-xs font-bold text-primary bg-primary/10 py-1 px-2 rounded inline-block" data-id="element-342" data-mp="6ln7jxc0wexp">
                          Duration: {phase.duration}
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Mobile Expand Hint */}
                  <div className="md:hidden mt-2 text-xs text-primary flex items-center justify-end" data-id="element-343" data-mp="bhlox0yxowom">
                    {activePhase !== phase.id && 'Tap for details'}
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 1
      }} className="text-center mb-24" data-id="element-344" data-mp="z8r55h8sdqzg">
          <p className="text-lg font-medium text-white bg-gradient-to-r from-transparent via-primary/20 to-transparent py-2" data-id="element-345" data-mp="2w3dqh8zqql5c">
            Most clients see results by{' '}
            <span className="text-primary font-bold" data-id="element-346" data-mp="1ghk0ip0axznl">Week 2</span>
          </p>
        </motion.div>

        {/* Team Section */}
        <div className="mb-24" data-id="element-347" data-mp="11tffheds1c1n">
          <h3 className="text-3xl font-bold text-center mb-12" data-id="element-348" data-mp="16sii6s6ab66g">Led By</h3>
          <div className="bg-dark-card border border-white/10 rounded-3xl overflow-hidden max-w-5xl mx-auto flex flex-col md:flex-row" data-id="element-349" data-mp="16qp9768789va">
            <div className="md:w-1/2 relative min-h-[400px]" data-id="element-350" data-mp="wv43oy3x8rgs">
              <img src="https://cdn.magicpatterns.com/uploads/qz61qp5bRViogfyQgchbuR/LetLois_People_(1).png" alt="Josh and Noah" className="absolute inset-0 w-full h-full object-cover" data-id="element-351" data-mp="wshyh54i6o1q" />
            </div>
            <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center space-y-8" data-id="element-352" data-mp="2da5n91xumbxd">
              <div data-id="element-353" data-mp="2i6vztfag2pe1">
                <h4 className="text-2xl font-bold text-white mb-2" data-id="element-354" data-mp="2i75uhg6fpkq2">Noah</h4>
                <p className="text-text-muted text-lg leading-relaxed" data-id="element-355" data-mp="37hilatys13ez">
                  The #1 appointment setter in the world ranked by data giant
                  ZoomInfo. Generating more appointments in one year than most
                  business owners do in a lifetime.
                </p>
              </div>
              <div className="w-full h-px bg-white/10" data-id="element-356" data-mp="33hi0mrl33vzh" />
              <div data-id="element-357" data-mp="3r61854wb84qi">
                <h4 className="text-2xl font-bold text-white mb-2" data-id="element-358" data-mp="3r7lxmg3roxhi">Josh</h4>
                <p className="text-text-muted text-lg leading-relaxed" data-id="element-359" data-mp="3v8o0d6ww7ro8">
                  A nerd who's scaled over 500 local businesses, with top
                  clients making the Inc 5000 multiple times.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Case Study Section */}
        <div data-id="element-360" data-mp="25sgatnxy0esn">
          <div className="text-center mb-12" data-id="element-361" data-mp="25rwq5zgwrcax">
            <h3 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-362" data-mp="2as15hm62y0zk">
              280% Growth. Less Than 4% Churn.
            </h3>
            <p className="text-xl text-text-muted" data-id="element-363" data-mp="237qjvl4tn9r7">
              Helping a global appointment coaching company scale in just two
              years.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-364" data-mp="2c9q4enf1ovyw">
            <div className="bg-white rounded-2xl p-4 shadow-xl overflow-hidden" data-id="element-365" data-mp="27an1p9mjf1u3">
              <img src="https://cdn.magicpatterns.com/uploads/8tMHMoaQgaWoJCfQnZMCPY/Case_Studies_16.png" alt="Stripe MRR Growth" className="w-full h-auto rounded-lg" data-id="element-366" data-mp="1jksmileqx16o" />
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-xl overflow-hidden" data-id="element-367" data-mp="1tbduv0k81k2s">
              <img src="https://cdn.magicpatterns.com/uploads/oh5K1ubg2cYiWmiQx83YDb/Case_Studies_14.png" alt="Stripe Volume Growth" className="w-full h-auto rounded-lg" data-id="element-368" data-mp="1ojneodyls23g" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}